import axios from "axios";
import { Download } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import i18n, { localeLoad, setLocaleSync, strings } from "@/I18n";
import {
  exchangePartnerToken,
  getPartnerViewAnalyticsConfig,
  getPartnerViewExportUrl,
} from "@/v2/apis/partnerView/partner-view-apis";
import { DateRangeFilterModern } from "@/v2/ui/composed/date-picker/DateRangeFilterModern";
import { Loading } from "@/v2/ui/composed/loading";
import { Button } from "@/v2/ui/primitives/button";
import { resolveDashboardTimeFilterId } from "@/v2/utils/supersetNativeFilters";
import { PartnerViewEmbed } from "./PartnerViewEmbed";

interface ReadyData {
  supersetUrl: string;
  dashboardId: string;
  snapshotAt: string | null;
  timeFilterId: string | null;
}

interface PartnerIdentity {
  partnerName: string;
  label: string | null;
}

type ExchangeStatus =
  | { kind: "loading" }
  | { kind: "rejected" }
  | { kind: "network-error" }
  | { kind: "ready"; data: ReadyData };

const TOKEN_FRAGMENT_PREFIX = "#token=";

// The partner view ships English + German (the operator platform's other
// locales aren't translated for partner-facing chrome yet). The picker is
// structured so adding a locale is just extending this list + the de.json.
const PARTNER_VIEW_LOCALES = ["en", "de"] as const;

/**
 * Partner-view locale: an explicit, previously-persisted choice wins; otherwise
 * default off the browser language so a German partner lands in German without
 * touching anything. The default derivation is read-only — it never writes — so
 * merely opening the partner view can't change a stored preference. (An explicit
 * switch does write `dashboard_locale`, which is shared with the operator
 * surface.) Returns one of PARTNER_VIEW_LOCALES.
 */
function pickInitialPartnerLocale(): string {
  const persisted = localStorage.getItem("dashboard_locale");
  if (
    persisted &&
    (PARTNER_VIEW_LOCALES as readonly string[]).includes(persisted)
  ) {
    return persisted;
  }
  return (navigator.language || "").toLowerCase().startsWith("de")
    ? "de"
    : "en";
}

// Default chrome date range = the last 30 days. The Superset dashboard time
// filter and the CSV/XLSX export both honour this on first load — no need
// for the partner to set anything before the first "this week" question.
const DEFAULT_RANGE_DAYS = 30;

function toIsoDate(d: Date): string {
  // Render in local time, not UTC. The partner picks "May 12" in their
  // wall-clock; a UTC string in May would already be a day off east of UTC.
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function computeDefaultRange(): { from: string; to: string } {
  const now = new Date();
  const to = toIsoDate(now);
  const fromDate = new Date(now);
  fromDate.setDate(fromDate.getDate() - DEFAULT_RANGE_DAYS);
  return { from: toIsoDate(fromDate), to };
}

/**
 * Standalone partner-view route. Lives outside the admin DashboardScreenV2
 * wrapper: no sidebar, no navbar, no Firebase auth check, no Redux user.
 *
 * Two entry paths:
 *   - First visit (token in fragment): exchange the token for a session
 *     cookie, scrub the fragment from the URL, then fetch the Superset
 *     config and render the embed.
 *   - Refresh / bookmark return (no token in fragment): try analytics_config
 *     directly. If the session cookie is still valid (within
 *     DEFAULT_SESSION_TTL_HOURS), this succeeds and we render the embed. If
 *     the cookie is missing or expired, the request 401s and the user is
 *     told the link is no longer active.
 */
export const PartnerView = () => {
  // Pure derivation from window.location — no useEffect needed for the read.
  // useMemo with empty deps so we don't re-read on re-render.
  const token = useMemo(() => {
    const hash = window.location.hash;
    if (!hash.startsWith(TOKEN_FRAGMENT_PREFIX)) return null;
    const raw = hash.slice(TOKEN_FRAGMENT_PREFIX.length);
    return raw.length > 0 ? raw : null;
  }, []);

  const [status, setStatus] = useState<ExchangeStatus>({ kind: "loading" });
  const [identity, setIdentity] = useState<PartnerIdentity | null>(null);
  const initialRange = useMemo(computeDefaultRange, []);
  const [dateFrom, setDateFrom] = useState<string>(initialRange.from);
  const [dateTo, setDateTo] = useState<string>(initialRange.to);

  // Set the i18n locale once, synchronously, before the chrome first renders,
  // so the wrapper (freshness label, action buttons, date formatting) is in the
  // partner's language from the first paint. Switching persists + reloads
  // (mirroring the operator switcher), so this initializer re-reads the choice.
  const [locale] = useState<string>(() => {
    const initial = pickInitialPartnerLocale();
    if (i18n.locale !== initial) setLocaleSync(initial);
    return initial;
  });

  const handleLocaleChange = useCallback((next: string) => {
    if (next === i18n.locale) return;
    void localeLoad(next);
    window.location.reload();
  }, []);

  // Hash-change defense. Token is captured once at mount via useMemo. The
  // fragment is intentionally kept in the URL after exchange (so partners can
  // refresh / bookmark / forward the link), so in the happy path the hash
  // stays put and this handler never fires. But if anything ever lands a
  // *different* `#token=...` on the same mounted page (in-app navigation,
  // address-bar paste, browser back/forward), the existing state would
  // survive: the cached identity / status / iframe from the prior session
  // would stay on screen even though the new token represents a different
  // partner-or-link. Force a hard reload so the new token goes through a
  // clean mount + exchange.
  useEffect(() => {
    const handler = () => {
      if (window.location.hash.startsWith(TOKEN_FRAGMENT_PREFIX)) {
        window.location.reload();
      }
    };
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);

  // First analytics_config response's guest_token is handed to the embed SDK
  // on its first fetch call, saving one round-trip on mount.
  const initialGuestTokenRef = useRef<string | null>(null);

  // Set Referrer-Policy: no-referrer for this document so outbound requests
  // (Superset iframe loads, external links) don't leak the route in the
  // Referer header. Production should also set this header at the hosting
  // layer for defense in depth — tracked as a follow-up.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  // Side effect: bootstrap the session. When the URL fragment has a token we
  // exchange it for a cookie first; either way, the final step is
  // analytics_config, which doubles as a cookie probe — its 401 is what tells
  // us a refreshed session has actually expired.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (token !== null) {
          // Keep the token in the URL fragment after exchange. We considered
          // scrubbing it (was the original design) but the UX cost is too
          // high for the billing-link use case:
          //   - refresh after the 24h session cookie expires leaves the
          //     partner stranded with no path back to the dashboard
          //   - the partner can't bookmark the URL for return visits
          //   - they can't forward the URL to a colleague at the same org
          // Keeping the token preserves the share/bookmark mental model
          // (same as Notion guest links, Figma share URLs). The token
          // remains revocable from the admin side and carries its own HMAC
          // + expiry, so a leaked URL is a containable incident rather
          // than a structural failure. See SECURITY.md for the threat-model
          // adjustment.
          const exchangeResp = await exchangePartnerToken(token);
          if (cancelled) return;
          setIdentity({
            partnerName: exchangeResp.partner_name,
            label: exchangeResp.label,
          });
        }

        const config = await getPartnerViewAnalyticsConfig();
        if (cancelled) return;
        initialGuestTokenRef.current = config.guest_token;
        setStatus({
          kind: "ready",
          data: {
            supersetUrl: config.superset_url,
            dashboardId: config.superset_dashboard_id,
            snapshotAt: config.data_snapshot_at,
            timeFilterId: resolveDashboardTimeFilterId(config.time_filter_id),
          },
        });
      } catch (err) {
        if (cancelled) return;
        const isHttpRejection =
          axios.isAxiosError(err) &&
          err.response !== undefined &&
          err.response.status >= 400 &&
          err.response.status < 500;
        setStatus({
          kind: isHttpRejection ? "rejected" : "network-error",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // Stable fetcher for the Superset SDK. First call returns the token already
  // fetched above; subsequent refreshes hit analytics_config again. The SDK
  // requires referential stability or it remounts the iframe on every render.
  const fetchGuestToken = useCallback(async () => {
    if (initialGuestTokenRef.current !== null) {
      const t = initialGuestTokenRef.current;
      initialGuestTokenRef.current = null;
      return t;
    }
    const config = await getPartnerViewAnalyticsConfig();
    return config.guest_token;
  }, []);

  const handleEmbedError = useCallback(() => {
    setStatus({ kind: "rejected" });
  }, []);

  // Document title carries the partner identity once exchange succeeds so
  // the browser tab is useful when a partner has the page bookmarked or
  // alongside other tabs. Reset to default on unmount so navigating away
  // (or the route being remounted on hash reload) doesn't leave stale text.
  useEffect(() => {
    const previousTitle = document.title;
    if (identity !== null) {
      document.title = identity.label
        ? `${identity.partnerName} — ${identity.label}`
        : identity.partnerName;
    }
    return () => {
      document.title = previousTitle;
    };
  }, [identity]);

  if (status.kind === "loading") {
    return (
      <div className="tw-h-screen tw-w-screen tw-flex tw-items-center tw-justify-center tw-bg-background">
        <Loading />
      </div>
    );
  }

  if (status.kind === "ready") {
    return (
      <div className="tw-h-screen tw-w-screen tw-flex tw-flex-col tw-bg-background">
        <PartnerViewChrome
          snapshotAt={status.data.snapshotAt}
          partnerName={identity?.partnerName ?? null}
          linkLabel={identity?.label ?? null}
          dateFrom={dateFrom}
          dateTo={dateTo}
          hasTimeFilter={status.data.timeFilterId !== null}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          locale={locale}
          onLocaleChange={handleLocaleChange}
        />
        <div className="tw-flex-1 tw-min-h-0">
          <PartnerViewEmbed
            supersetUrl={status.data.supersetUrl}
            dashboardId={status.data.dashboardId}
            onEmbedError={handleEmbedError}
            fetchGuestToken={fetchGuestToken}
            dateFrom={dateFrom}
            dateTo={dateTo}
            timeFilterId={status.data.timeFilterId}
            containerClassName="tw-h-full tw-w-full"
          />
        </div>
      </div>
    );
  }

  return <PartnerViewErrorPage variant={status.kind} />;
};

// > this many hours past last refresh = warn the partner the snapshot is stale.
// Cron runs daily at 03:00 UTC, so a healthy snapshot is at most ~27h old at the
// instant before the next run; bumping to 30 covers an hour of cron drift before
// we cry wolf.
const STALE_HOURS = 30;

interface PartnerViewFreshnessProps {
  snapshotAt: string | null;
}

/**
 * Tells the partner when the snapshot was last refreshed by the daily cron.
 *
 * Two readability concerns the label has to address at once:
 *
 *   1. "Last refreshed Mar 23 03:00" reads like a continuous live signal —
 *      partners assume new rides land within minutes. The chrome makes the
 *      daily-snapshot cadence explicit so they don't refresh the page in
 *      vain waiting for a ride that ended an hour ago to appear.
 *   2. "Data through" would imply a hard guarantee that anything before
 *      the cutoff is reflected — but the materializer reads source rides
 *      by `start_time`, not `end_time`, so a ride that started before the
 *      cutoff but ended after still won't appear until the next refresh.
 *
 * "Daily snapshot · last refreshed …" answers both: the cadence is named
 * up front, and the value is the verifiable event ("we ran"), not a claim
 * about what's necessarily included.
 */
const PartnerViewFreshness = ({ snapshotAt }: PartnerViewFreshnessProps) => {
  if (snapshotAt === null) return null;

  const parsed = new Date(snapshotAt);
  if (Number.isNaN(parsed.getTime())) return null;

  const ageHours = (Date.now() - parsed.getTime()) / 3_600_000;
  const stale = ageHours > STALE_HOURS;

  // Render in the partner's own timezone so the time matches the wall clock
  // they recognise. `timeZoneName: "short"` appends e.g. "CEST" / "GMT" so the
  // value stays unambiguous when copied between zones.
  const formatter = new Intl.DateTimeFormat(i18n.locale || undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZoneName: "short",
  });
  const roundedHours = Math.round(ageHours);
  const text = strings("partnerView.freshnessLabel", {
    time: formatter.format(parsed),
  });

  return (
    <span
      className={
        stale
          ? "tw-text-xs tw-text-destructive tw-mr-2"
          : "tw-text-xs tw-text-muted-foreground tw-mr-2"
      }
      data-testid="partner-view-freshness"
      title={
        stale
          ? strings("partnerView.freshnessStaleTooltip", {
              hours: roundedHours,
            })
          : strings("partnerView.freshnessTooltip")
      }
    >
      {stale
        ? `⚠ ${text} ${strings("partnerView.freshnessStaleSuffix", { hours: roundedHours })}`
        : text}
    </span>
  );
};

interface PartnerViewChromeProps {
  snapshotAt: string | null;
  /**
   * Partner name from the exchange response. When provided, drives the chrome
   * heading ("Demo Oslo Sentrum — Operator Report") and the document title —
   * replacing the generic Superset dashboard name ("Partner Report") which is
   * shared across all fleets and not useful as a per-partner identifier.
   */
  partnerName: string | null;
  /**
   * Optional human-set label on the access link itself (eg "Sigo Support",
   * "Würzburg Q2 Billing"). When present, takes precedence over the
   * dashboard-derived "Operator Report" suffix so admins can customise the
   * partner-facing title per link.
   */
  linkLabel: string | null;
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  hasTimeFilter: boolean;
  locale: string;
  onLocaleChange: (value: string) => void;
}

// Local ISO (YYYY-MM-DD) ⇄ Date helpers used by the chrome's date picker
// trigger. The chrome stores ISO strings (URL- and export-friendly); the
// shared DatePicker speaks `Date` objects.
function isoToDate(iso: string): Date | null {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function dateToIso(d: Date | null): string {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Slim header above the embed. Carries:
 *  - the freshness chip (when the snapshot was last refreshed)
 *  - a [from, to] date range picker that drives both the dashboard's time
 *    filter and the CSV/XLSX export URL
 *  - CSV + XLSX download buttons (plain anchors — same-origin GET picks up
 *    the SameSite=Strict session cookie so the browser handles the file save
 *    via Content-Disposition; no JS download dance)
 *
 * The date range is applied to the dashboard via the Superset embed SDK's
 * `urlParams` (passed down to PartnerViewEmbed) AND appended to the export
 * URL as ``?from=...&to=...`` query params, so the partner sees consistent
 * data on screen and in any downloaded file. The export endpoint enforces
 * RLS independently — the date range narrows results, it doesn't widen them.
 */
const PartnerViewChrome = ({
  snapshotAt,
  partnerName,
  linkLabel,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  hasTimeFilter,
  locale,
  onLocaleChange,
}: PartnerViewChromeProps) => {
  // Title precedence: human-set link label > "{partnerName} — Operator Report"
  // > nothing. Falling back to nothing (not "Partner Report") because the
  // generic Superset name is what we hid via hideTitle: true — leaking it
  // back into the chrome would defeat that.
  const title = linkLabel?.trim()
    ? linkLabel
    : partnerName
      ? strings("partnerView.operatorReportSuffix", { partnerName })
      : null;

  const dateFromObj = isoToDate(dateFrom);
  const dateToObj = isoToDate(dateTo);
  // Build the export URL with the date range appended. If either end is
  // missing we omit it from the URL so the backend falls back to "no bound
  // on that side" (covered by ``_parse_date`` returning None on empty input).
  const exportUrl = (format: "csv" | "xlsx") => {
    const base = getPartnerViewExportUrl(format);
    const params = new URLSearchParams();
    if (hasTimeFilter && dateFrom) params.set("from", dateFrom);
    if (hasTimeFilter && dateTo) params.set("to", dateTo);
    const qs = params.toString();
    return qs ? `${base}&${qs}` : base;
  };

  return (
    <div className="tw-px-4 tw-py-2 tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-gap-y-2 tw-border-b tw-border-border tw-bg-background tw-shrink-0">
      {title && (
        <h1
          className="tw-text-base tw-font-semibold tw-text-foreground tw-m-0 tw-mr-auto tw-truncate"
          data-testid="partner-view-title"
          title={title}
        >
          {title}
        </h1>
      )}
      <PartnerViewFreshness snapshotAt={snapshotAt} />
      {/*
       * Same picker as the admin analytics-v2 dashboard
       * (SupersetDashboardContainer.tsx). Quick presets ("Last 30 days",
       * "Last month", "This year"...), month-jump calendar, single
       * popover — the native `<input type="date">` previously here had no
       * month-jumper and forced day-by-day arrow nav, which broke for
       * back-month billing flows where the partner picks "March 2026"
       * three months back.
       */}
      {hasTimeFilter && (
        <DateRangeFilterModern
          dateFrom={dateFromObj}
          dateTo={dateToObj}
          onDateFromChange={(d) => onDateFromChange(dateToIso(d))}
          onDateToChange={(d) => onDateToChange(dateToIso(d))}
          showRefresh={false}
        />
      )}
      <Button
        asChild={true}
        variant="outline"
        size="sm"
        data-testid="partner-view-export-csv"
      >
        <a href={exportUrl("csv")} download={true}>
          <Download className="tw-mr-2 tw-h-4 tw-w-4" />
          {strings("partnerView.exportCsvLabel")}
        </a>
      </Button>
      <Button
        asChild={true}
        variant="default"
        size="sm"
        data-testid="partner-view-export-xlsx"
      >
        <a href={exportUrl("xlsx")} download={true}>
          <Download className="tw-mr-2 tw-h-4 tw-w-4" />
          {strings("partnerView.exportXlsxLabel")}
        </a>
      </Button>
      <fieldset
        className="tw-flex tw-items-center tw-gap-1 tw-border-0 tw-p-0 tw-m-0 tw-min-w-0"
        aria-label={strings("partnerView.languageLabel")}
        data-testid="partner-view-language"
      >
        {PARTNER_VIEW_LOCALES.map((lng) => (
          <Button
            key={lng}
            variant={lng === locale ? "default" : "outline"}
            size="sm"
            onClick={() => onLocaleChange(lng)}
            aria-pressed={lng === locale}
          >
            {lng.toUpperCase()}
          </Button>
        ))}
      </fieldset>
    </div>
  );
};

interface PartnerViewErrorPageProps {
  variant: "rejected" | "network-error";
}

const PartnerViewErrorPage = ({ variant }: PartnerViewErrorPageProps) => {
  const title =
    variant === "network-error"
      ? strings("partnerView.errorNetworkTitle")
      : strings("partnerView.errorRejectedTitle");

  const body =
    variant === "network-error"
      ? strings("partnerView.errorNetworkBody")
      : strings("partnerView.errorRejectedBody");

  return (
    <div className="tw-h-screen tw-w-screen tw-flex tw-items-center tw-justify-center tw-bg-background tw-px-6">
      <div className="tw-max-w-md tw-text-center tw-space-y-3">
        <h1 className="tw-text-xl tw-font-semibold tw-text-foreground tw-m-0">
          {title}
        </h1>
        <p className="tw-text-sm tw-text-muted-foreground tw-m-0">{body}</p>
      </div>
    </div>
  );
};

export default PartnerView;
